// 1. إزالة ReactNode لأنه غير مستخدم مباشرة هنا
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import * as notificationService from '../services/notificationService';
import { Notification } from '../types';

interface AppContextType {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  notifications: Notification[];
  unreadCount: number;
  fetchNotifications: () => void;
  markNotificationAsRead: (id: number) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
}

// تعريف السياق يبقى كما هو
export const AppContext = createContext<AppContextType | undefined>(undefined);

// استخدام React.PropsWithChildren هو طريقة حديثة لتضمين children
export const AppProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(window.innerWidth > 992);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();

  const toggleSidebar = () => setSidebarOpen(!isSidebarOpen);

  // 2. تغليف كل الدوال بـ useCallback
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      // استخدام Promise.all لجلب البيانات بالتوازي
      const [notifs, count] = await Promise.all([
        notificationService.getMyNotifications(user.id),
        notificationService.getUnreadCount(user.id),
      ]);
      setNotifications(notifs);
      setUnreadCount(count);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, [user]); // هذه الدالة تعتمد فقط على 'user'

  const markNotificationAsRead = useCallback(async (id: number) => {
    try {
      await notificationService.markAsRead(id);
      // استدعاء fetchNotifications بعد النجاح
      fetchNotifications(); 
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  }, [fetchNotifications]); // تعتمد على 'fetchNotifications'

  const markAllNotificationsAsRead = useCallback(async () => {
    if (!user) return;
    try {
      await notificationService.markAllAsRead(user.id);
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  }, [user, fetchNotifications]); // تعتمد على 'user' و 'fetchNotifications'

  // 3. إصلاح useEffect
  useEffect(() => {
    if (user) {
      fetchNotifications(); // استدعاء أولي
      
      // جلب الإشعارات كل دقيقة
      const interval = setInterval(() => {
        fetchNotifications();
      }, 60000);
      
      // دالة التنظيف لإيقاف الـ interval عند إزالة المكون
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]); // !! إضافة fetchNotifications إلى مصفوفة الاعتماديات

  const value = {
    isSidebarOpen,
    toggleSidebar,
    notifications,
    unreadCount,
    fetchNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// hook استخدام السياق يبقى كما هو
export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
